import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getAllUsers } from '../../services/users';
import { createGroupConversation } from '../../services/api/messages';
import { useAuth } from '../../hooks/useAuth';
import { getInitials } from '../../lib/initials';

interface NewConversationModalProps {
  onClose: () => void;
}

export function NewConversationModal({ onClose }: NewConversationModalProps) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [selected, setSelected] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: getAllUsers,
  });

  const others = allUsers.filter((u) => u.id !== currentUser?.id);

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const createMutation = useMutation({
    mutationFn: () => createGroupConversation(selected, groupName.trim() || undefined),
    onSuccess: (data) => {
      navigate(`/messages/group/${data.id}`);
      onClose();
    },
  });

  const handleConfirm = () => {
    if (selected.length === 1) {
      navigate(`/messages/${selected[0]}`);
      onClose();
    } else if (selected.length >= 2) {
      createMutation.mutate();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white w-full max-w-sm rounded-xl shadow-2xl flex flex-col"
        style={{ maxHeight: 'min(480px, 85vh)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">Ny besked</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* User list */}
        <div className="overflow-y-auto flex-1">
          {others.map((u) => {
            const isSelected = selected.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggle(u.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-left ${
                  isSelected ? 'bg-primary-green/5' : ''
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {u.profileImageUrl ? (
                    <img src={u.profileImageUrl} alt={u.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-semibold text-gray-500">{getInitials(u.name)}</span>
                  )}
                </div>
                <span className="flex-1 text-sm font-medium text-gray-900">{u.name}</span>
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-primary-green border-primary-green' : 'border-gray-300'
                  }`}
                >
                  {isSelected && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Group name (only for group) */}
        {selected.length >= 2 && (
          <div className="px-4 py-2 border-t flex-shrink-0">
            <input
              type="text"
              placeholder="Gruppenavn (valgfrit)"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
            />
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-3 border-t flex-shrink-0">
          <button
            onClick={handleConfirm}
            disabled={selected.length === 0 || createMutation.isPending}
            className="w-full py-2 bg-primary-green text-white text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary-green transition-colors"
          >
            {createMutation.isPending
              ? 'Opretter...'
              : selected.length === 0
              ? 'Vælg en deltager'
              : selected.length === 1
              ? 'Åbn samtale'
              : `Opret gruppe (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
